import React, { useEffect, useState } from 'react';
import { States } from '@/helpers/common/states';
import validators from '@/helpers/validators';
import useForm from '@/hooks/useForm';
import api from '@/services/api';
import CompanyParameter from '@/model/CompanyParameter';
import CardFees from '@/model/CardFees';
import { toast } from 'react-toastify';
import { unmask, updateMask } from '@/helpers/masks/cashNumber';
import { CardFeesInstallment } from '@/model/CardFeesInstallment';
import { CardFeeContainer } from './ui';
import { FormInputName, InstallmentsFeeType, InstallmentFeeForm } from '../types';

export const CardFeeScreen: React.FC = (): JSX.Element => {
  const [state, setState] = useState(States.default);
  const [ticketPhysicalSaleInstallment, setTicketPhysicalSaleInstallment] = useState<
    InstallmentFeeForm[]
  >([]);
  const [ticketWebsiteSaleInstallment, setTicketWebsiteSaleInstallment] = useState<
    InstallmentFeeForm[]
  >([]);
  const [productPhysicalSaleInstallment, setProductPhysicalSaleInstallment] = useState<
    InstallmentFeeForm[]
  >([]);
  const [productWebsiteSaleInstallment, setProductWebsiteSaleInstallment] = useState<
    InstallmentFeeForm[]
  >([]);
  const { formData, formErrors, onChangeFormInput, isFormValid } = useForm({
    initialData: {
      ticketPhysicalSaleId: '',
      ticketPhysicalSaleDebit: '',
      ticketPhysicalSaleCredit: '',
      ticketPhysicalSalePix: '',
      ticketPhysicalSaleAdministrateTax: '',
      ticketPhysicalSaleInstallments: '0',
      ticketWebsiteSaleId: '',
      ticketWebsiteSaleBankSlip: '',
      ticketWebsiteSaleCredit: '',
      ticketWebsiteSalePix: '',
      ticketWebsiteSaleAdministrateTax: '',
      ticketWebsiteSaleInstallments: '0',
      productPhysicalSaleId: '',
      productPhysicalSaleDebit: '',
      productPhysicalSaleCredit: '',
      productPhysicalSalePix: '',
      productPhysicalSaleAdministrateTax: '',
      productPhysicalSaleInstallments: '0',
      productWebsiteSaleId: '',
      productWebsiteSaleBankSlip: '',
      productWebsiteSaleCredit: '',
      productWebsiteSalePix: '',
      productWebsiteSaleAdministrateTax: '',
      productWebsiteSaleInstallments: '0',
    },
    validators: {
      ticketPhysicalSaleDebit: [
        validators.required,
        validators.applyUnmask(validators.between(0, 99.99), unmask),
      ],
      ticketPhysicalSaleCredit: [
        validators.required,
        validators.applyUnmask(validators.between(0, 99.99), unmask),
      ],
      ticketPhysicalSalePix: [
        validators.required,
        validators.applyUnmask(validators.between(0, 99.999), unmask),
      ],
      ticketPhysicalSaleAdministrateTax: [
        validators.required,
        validators.applyUnmask(validators.between(0, 99.99), unmask),
      ],
      ticketPhysicalSaleInstallments: [validators.required],
      ticketWebsiteSaleBankSlip: [
        validators.required,
        validators.applyUnmask(validators.between(0, 99.99), unmask),
      ],
      ticketWebsiteSaleCredit: [
        validators.required,
        validators.applyUnmask(validators.between(0, 99.99), unmask),
      ],
      ticketWebsiteSalePix: [
        validators.required,
        validators.applyUnmask(validators.between(0, 99.99), unmask),
      ],
      ticketWebsiteSaleAdministrateTax: [
        validators.required,
        validators.applyUnmask(validators.between(0, 99.99), unmask),
      ],
      ticketWebsiteSaleInstallments: [validators.required],
      productPhysicalSaleDebit: [
        validators.required,
        validators.applyUnmask(validators.between(0, 99.99), unmask),
      ],
      productPhysicalSaleCredit: [
        validators.required,
        validators.applyUnmask(validators.between(0, 99.99), unmask),
      ],
      productPhysicalSalePix: [
        validators.required,
        validators.applyUnmask(validators.between(0, 99.999), unmask),
      ],
      productPhysicalSaleAdministrateTax: [
        validators.required,
        validators.applyUnmask(validators.between(0, 99.99), unmask),
      ],
      productPhysicalSaleInstallments: [validators.required],
      productWebsiteSaleBankSlip: [
        validators.required,
        validators.applyUnmask(validators.between(0, 99.99), unmask),
      ],
      productWebsiteSaleCredit: [
        validators.required,
        validators.applyUnmask(validators.between(0, 99.99), unmask),
      ],
      productWebsiteSalePix: [
        validators.required,
        validators.applyUnmask(validators.between(0, 99.99), unmask),
      ],
      productWebsiteSaleAdministrateTax: [
        validators.required,
        validators.applyUnmask(validators.between(0, 99.99), unmask),
      ],
      productWebsiteSaleInstallments: [validators.required],
    },
    formatters: {
      ticketPhysicalSaleDebit: updateMask,
      ticketPhysicalSaleCredit: updateMask,
      ticketPhysicalSalePix: updateMask,
      ticketPhysicalSaleAdministrateTax: updateMask,
      ticketWebsiteSaleBankSlip: updateMask,
      ticketWebsiteSaleCredit: updateMask,
      ticketWebsiteSalePix: updateMask,
      ticketWebsiteSaleAdministrateTax: updateMask,
      productPhysicalSaleDebit: updateMask,
      productPhysicalSaleCredit: updateMask,
      productPhysicalSalePix: updateMask,
      productPhysicalSaleAdministrateTax: updateMask,
      productWebsiteSaleBankSlip: updateMask,
      productWebsiteSaleCredit: updateMask,
      productWebsiteSalePix: updateMask,
      productWebsiteSaleAdministrateTax: updateMask,
    },
  });

  const handleOnGetCardFee = async (): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.get<CompanyParameter>('/company/parameter/fee');
      if (data.ticketPhysicalSale) {
        onChangeFormInput(FormInputName.ticketPhysicalSaleId)(String(data.ticketPhysicalSale.id));
        onChangeFormInput(FormInputName.ticketPhysicalSaleDebit)(
          String(Number(data.ticketPhysicalSale.debit * 100).toFixed(0)),
        );
        onChangeFormInput(FormInputName.ticketPhysicalSaleCredit)(
          String(Number(data.ticketPhysicalSale.credit * 100).toFixed(0)),
        );
        onChangeFormInput(FormInputName.ticketPhysicalSalePix)(
          String(Number(data.ticketPhysicalSale.pix * 100).toFixed(0)),
        );
        onChangeFormInput(FormInputName.ticketPhysicalSaleAdministrateTax)(
          String(Number(data.ticketPhysicalSale.administrateTax * 100).toFixed(0)),
        );
        const installments = (data.ticketPhysicalSale.installmentFees ?? [])
          .map(item => ({
            installment: Number(item.installment),
            fee: updateMask((Number(item.fee) * 100).toFixed(0)),
          }))
          .sort((a, b) => a.installment - b.installment);
        onChangeFormInput(FormInputName.ticketPhysicalSaleInstallments)(
          String(Math.max(0, ...installments.map(item => item.installment))),
        );
        setTicketPhysicalSaleInstallment(installments);
      }
      if (data.ticketWebsiteSale) {
        onChangeFormInput(FormInputName.ticketWebsiteSaleId)(String(data.ticketWebsiteSale.id));
        onChangeFormInput(FormInputName.ticketWebsiteSaleBankSlip)(
          String(Number(data.ticketWebsiteSale.bankSlip * 100).toFixed(0)),
        );
        onChangeFormInput(FormInputName.ticketWebsiteSaleCredit)(
          String(Number(data.ticketWebsiteSale.credit * 100).toFixed(0)),
        );
        onChangeFormInput(FormInputName.ticketWebsiteSalePix)(
          String(Number(data.ticketWebsiteSale.pix * 100).toFixed(0)),
        );
        onChangeFormInput(FormInputName.ticketWebsiteSaleAdministrateTax)(
          String(Number(data.ticketWebsiteSale.administrateTax * 100).toFixed(0)),
        );
        const installments = (data.ticketWebsiteSale.installmentFees ?? [])
          .map(item => ({
            installment: Number(item.installment),
            fee: updateMask((Number(item.fee) * 100).toFixed(0)),
          }))
          .sort((a, b) => a.installment - b.installment);
        onChangeFormInput(FormInputName.ticketWebsiteSaleInstallments)(
          String(Math.max(0, ...installments.map(item => item.installment))),
        );
        setTicketWebsiteSaleInstallment(installments);
      }
      if (data.productPhysicalSale) {
        onChangeFormInput(FormInputName.productPhysicalSaleId)(String(data.productPhysicalSale.id));
        onChangeFormInput(FormInputName.productPhysicalSaleDebit)(
          String(Number(data.productPhysicalSale.debit * 100).toFixed(0)),
        );
        onChangeFormInput(FormInputName.productPhysicalSaleCredit)(
          String(Number(data.productPhysicalSale.credit * 100).toFixed(0)),
        );
        onChangeFormInput(FormInputName.productPhysicalSalePix)(
          String(Number(data.productPhysicalSale.pix * 100).toFixed(0)),
        );
        onChangeFormInput(FormInputName.productPhysicalSaleAdministrateTax)(
          String(Number(data.productPhysicalSale.administrateTax * 100).toFixed(0)),
        );
        const installments = (data.productPhysicalSale.installmentFees ?? [])
          .map(item => ({
            installment: Number(item.installment),
            fee: updateMask((Number(item.fee) * 100).toFixed(0)),
          }))
          .sort((a, b) => a.installment - b.installment);
        onChangeFormInput(FormInputName.productPhysicalSaleInstallments)(
          String(Math.max(0, ...installments.map(item => item.installment))),
        );
        setProductPhysicalSaleInstallment(installments);
      }
      if (data.productWebsiteSale) {
        onChangeFormInput(FormInputName.productWebsiteSaleId)(String(data.productWebsiteSale.id));
        onChangeFormInput(FormInputName.productWebsiteSaleBankSlip)(
          String(Number(data.productWebsiteSale.bankSlip * 100).toFixed(0)),
        );
        onChangeFormInput(FormInputName.productWebsiteSaleCredit)(
          String(Number(data.productWebsiteSale.credit * 100).toFixed(0)),
        );
        onChangeFormInput(FormInputName.productWebsiteSalePix)(
          String(Number(data.productWebsiteSale.pix * 100).toFixed(0)),
        );
        onChangeFormInput(FormInputName.productWebsiteSaleAdministrateTax)(
          String(Number(data.productWebsiteSale.administrateTax * 100).toFixed(0)),
        );
        const installments = (data.productWebsiteSale.installmentFees ?? [])
          .map(item => ({
            installment: Number(item.installment),
            fee: updateMask((Number(item.fee) * 100).toFixed(0)),
          }))
          .sort((a, b) => a.installment - b.installment);
        onChangeFormInput(FormInputName.productWebsiteSaleInstallments)(
          String(Math.max(0, ...installments.map(item => item.installment))),
        );
        setProductWebsiteSaleInstallment(installments);
      }
    } finally {
      setState(States.default);
    }
  };

  const installmentSetters = {
    [InstallmentsFeeType.ticketPhysicalSale]: setTicketPhysicalSaleInstallment,
    [InstallmentsFeeType.ticketWebsiteSale]: setTicketWebsiteSaleInstallment,
    [InstallmentsFeeType.productPhysicalSale]: setProductPhysicalSaleInstallment,
    [InstallmentsFeeType.productWebsiteSale]: setProductWebsiteSaleInstallment,
  };

  const handleOnChangeFee = (index: number, type: InstallmentsFeeType, value: string): void => {
    installmentSetters[type](previous =>
      previous.map((item, ix) =>
        ix === index ? { ...item, fee: value.trim() ? updateMask(value) : '' } : item,
      ),
    );
  };

  const handleOnChangeInstallments = (type: InstallmentsFeeType, value: number): void => {
    if (!Number.isInteger(value) || value < 0) return;
    onChangeFormInput(`${type}Installments`)(String(value));
    installmentSetters[type](previous =>
      Array.from({ length: value }, (_, index) => ({
        installment: index + 1,
        fee: previous.find(item => item.installment === index + 1)?.fee ?? updateMask('0'),
      })),
    );
  };

  const serializeInstallments = (items: InstallmentFeeForm[]): CardFeesInstallment[] =>
    items
      .map(item => ({ installment: item.installment, fee: Number(unmask(item.fee)) }))
      .sort((a, b) => a.installment - b.installment);

  const mountTicketPhysicalSale = (): CardFees => {
    const installmentFees = serializeInstallments(ticketPhysicalSaleInstallment);
    const physicalSale = {
      debit: +unmask(formData[FormInputName.ticketPhysicalSaleDebit]),
      credit: +unmask(formData[FormInputName.ticketPhysicalSaleCredit]),
      pix: +unmask(formData[FormInputName.ticketPhysicalSalePix]),
      administrateTax: +unmask(formData[FormInputName.ticketPhysicalSaleAdministrateTax]),
      installmentFees,
    } as CardFees;
    if (
      formData[FormInputName.ticketPhysicalSaleId] &&
      formData[FormInputName.ticketPhysicalSaleId].trim().length > 0
    ) {
      physicalSale.id = formData[FormInputName.ticketPhysicalSaleId];
    }
    return physicalSale;
  };

  const mountTicketWebsiteSale = (): CardFees => {
    const installmentFees = serializeInstallments(ticketWebsiteSaleInstallment);
    const websiteSale = {
      credit: +unmask(formData[FormInputName.ticketWebsiteSaleCredit]),
      bankSlip: +unmask(formData[FormInputName.ticketWebsiteSaleBankSlip]),
      pix: +unmask(formData[FormInputName.ticketWebsiteSalePix]),
      administrateTax: +unmask(formData[FormInputName.ticketWebsiteSaleAdministrateTax]),
      installmentFees,
    } as CardFees;
    if (
      formData[FormInputName.ticketWebsiteSaleId] &&
      formData[FormInputName.ticketWebsiteSaleId].trim().length > 0
    ) {
      websiteSale.id = formData[FormInputName.ticketWebsiteSaleId];
    }
    return websiteSale;
  };

  const mountProductPhysicalSale = (): CardFees => {
    const installmentFees = serializeInstallments(productPhysicalSaleInstallment);
    const physicalSale = {
      debit: +unmask(formData[FormInputName.productPhysicalSaleDebit]),
      credit: +unmask(formData[FormInputName.productPhysicalSaleCredit]),
      pix: +unmask(formData[FormInputName.productPhysicalSalePix]),
      administrateTax: +unmask(formData[FormInputName.productPhysicalSaleAdministrateTax]),
      installmentFees,
    } as CardFees;
    if (
      formData[FormInputName.productPhysicalSaleId] &&
      formData[FormInputName.productPhysicalSaleId].trim().length > 0
    ) {
      physicalSale.id = formData[FormInputName.productPhysicalSaleId];
    }
    return physicalSale;
  };

  const mountProductWebsiteSale = (): CardFees => {
    const installmentFees = serializeInstallments(productWebsiteSaleInstallment);
    const websiteSale = {
      credit: +unmask(formData[FormInputName.productWebsiteSaleCredit]),
      bankSlip: +unmask(formData[FormInputName.productWebsiteSaleBankSlip]),
      pix: +unmask(formData[FormInputName.productWebsiteSalePix]),
      administrateTax: +unmask(formData[FormInputName.productWebsiteSaleAdministrateTax]),
      installmentFees,
    } as CardFees;
    if (
      formData[FormInputName.productWebsiteSaleId] &&
      formData[FormInputName.productWebsiteSaleId].trim().length > 0
    ) {
      websiteSale.id = formData[FormInputName.productWebsiteSaleId];
    }
    return websiteSale;
  };

  const handleOnSave = async (): Promise<void> => {
    const validForm = isFormValid();
    const installments = [
      ticketPhysicalSaleInstallment,
      ticketWebsiteSaleInstallment,
      productPhysicalSaleInstallment,
      productWebsiteSaleInstallment,
    ];
    const validInstallments = installments.every(
      items =>
        new Set(items.map(item => item.installment)).size === items.length &&
        items.every(
          item =>
            Number.isInteger(item.installment) &&
            item.installment > 0 &&
            item.fee.trim() !== '' &&
            Number.isFinite(Number(unmask(item.fee))) &&
            Number(unmask(item.fee)) >= 0,
        ),
    );
    if (!validInstallments) toast.error('Informe uma taxa válida para cada parcela.');
    if (validForm && validInstallments) {
      try {
        setState(States.loading);
        const dto: CompanyParameter = {
          ticketPhysicalSale: mountTicketPhysicalSale(),
          ticketWebsiteSale: mountTicketWebsiteSale(),
          productPhysicalSale: mountProductPhysicalSale(),
          productWebsiteSale: mountProductWebsiteSale(),
        };

        await api.post<void>('/company/parameter/fee', dto);

        toast.success('Taxas configuradas com sucesso!');
        await handleOnGetCardFee();
      } finally {
        setState(States.default);
      }
    }
  };

  useEffect(() => {
    handleOnGetCardFee();
  }, []);

  return (
    <CardFeeContainer
      state={state}
      formData={formData}
      formErrors={formErrors}
      onChangeFormInput={onChangeFormInput}
      ticketPhysicalSaleInstallment={ticketPhysicalSaleInstallment}
      onChangeInstallments={handleOnChangeInstallments}
      onChangeFee={handleOnChangeFee}
      ticketWebsiteSaleInstallment={ticketWebsiteSaleInstallment}
      productPhysicalSaleInstallment={productPhysicalSaleInstallment}
      productWebsiteSaleInstallment={productWebsiteSaleInstallment}
      onSave={handleOnSave}
    />
  );
};
